import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";

import { ViaIcon } from "@/shared/components/ViaIcon";
import { TEMPLATES } from "../blocks";
import { duplicate, freshSteps, isFull, moved, newStep, type DraftStep } from "../steps";
import { AddStep } from "./AddStep";
import { StepCard } from "./StepCard";

import type { InterfaceType, OnboardingBlock } from "@/shared/types";

/** Where someone new comes from: the client app and the chat channels. */
const ENTRY: InterfaceType[] = ["web", "whatsapp", "telegram", "slack", "discord", "teams"];

/* The flow drawn top to bottom on the dot field: someone new writes, takes each step in
   order, and the agent answers. A "+" sits on every gap of the line; an empty flow offers
   templates under it. */
export function FlowCanvas({ steps, selected, agentName, onSelect, onEdit, onRemove }: {
  steps: DraftStep[];
  selected: string | null;
  agentName: string;
  onSelect: (uid: string) => void;
  onEdit: (steps: DraftStep[]) => void;
  onRemove: (uid: string) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const full = isFull(steps);
  const hasLogin = steps.some((s) => s.type === "login");

  const insert = (at: number, added: DraftStep[]) => {
    onEdit([...steps.slice(0, at), ...added, ...steps.slice(at)]);
    onSelect(added[0].uid);
  };
  const add = (at: number) => (type: OnboardingBlock["type"]) => insert(at, [newStep(type)]);

  return (
    <section
      aria-label="Flow"
      className="flex-[0_1_380px] min-w-[300px] flex flex-col rounded-[18px] ring-1 ring-[var(--hairline)] bg-[var(--surface-sunk)] wf-dots px-4 pt-5 pb-6"
    >
      <div className="self-center flex items-center gap-1.5 h-[30px] pl-1.5 pr-3 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] text-[12.5px] text-[var(--text-secondary)] whitespace-nowrap">
        <span className="flex gap-0.5">
          {ENTRY.map((type) => <ViaIcon key={type} type={type} />)}
        </span>
        Someone new writes
      </div>
      <AddStep hasLogin={hasLogin} full={full} onAdd={add(0)} />
      {steps.map((step, i) => (
        <React.Fragment key={step.uid}>
          <StepCard
            step={step}
            index={i}
            count={steps.length}
            selected={step.uid === selected}
            full={full}
            onSelect={() => onSelect(step.uid)}
            onMove={(to) => onEdit(moved(steps, i, to))}
            onDuplicate={() => step.type === "questions" && insert(i + 1, [duplicate(step)])}
            onRemove={() => onRemove(step.uid)}
            drag={{
              armed: armed === step.uid,
              dragging: dragging === i,
              onArm: () => setArmed(step.uid),
              onStart: () => setDragging(i),
              onOver: () => {
                if (dragging === null || dragging === i) return;
                onEdit(moved(steps, dragging, i));
                setDragging(i);
              },
              onEnd: () => {
                setDragging(null);
                setArmed(null);
              },
            }}
          />
          <AddStep hasLogin={hasLogin} full={full} onAdd={add(i + 1)} />
        </React.Fragment>
      ))}
      <div className="self-center flex items-center gap-1.5 h-[30px] pl-2 pr-3 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] text-[12.5px] text-[var(--text-secondary)] whitespace-nowrap">
        <Check className="w-[13px] h-[13px] shrink-0 text-[var(--success)]" strokeWidth={2} />
        {agentName} answers
      </div>
      {steps.length === 0 && (
        <div className="mt-5 flex flex-col gap-2">
          <p className="m-0 text-[12px] leading-[1.5] text-center text-[var(--text-tertiary)]">
            No steps: {agentName} answers everyone it lets in straight away. Press + or start from a template.
          </p>
          {TEMPLATES.map((template) => (
            <button
              key={template.name}
              type="button"
              aria-label={`Use ${template.name}`}
              onClick={() => {
                const added = freshSteps(template.blocks);
                onEdit(added);
                onSelect(added[0].uid);
                toast.success(`${template.name} added`, { description: "Edit it, then save the flow." });
              }}
              className="press card-hover flex items-center gap-2.5 px-3 py-2.5 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-[var(--text-primary)]">{template.name}</span>
                <span className="block mt-px text-[11.5px] text-[var(--text-tertiary)]">{template.says}</span>
              </span>
              <span className="text-[12px] font-medium text-[var(--text-secondary)]">Use</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
